import React, { useMemo } from 'react'
import { Box, Grid, IconButton, Tooltip } from '@mui/material'
import Mode from '@mui/icons-material/Mode'
import { createColumnHelper } from '@tanstack/react-table'
import {
  AdGroupBudgetPlanPartsFragment,
  AdGroupBudgetPlansDocument,
  BudgetCapping,
  CostType,
  DailyBudgetType,
  useDeleteAdGroupBudgetPlanMutation,
} from '../../../../../__generated__/graphql'
import DeleteCellAction from '../../../../../components/table/DeleteCellAction'
import ReactTable, { Column } from '../../../../../components/table/ReactTable'
import { StyledMessage } from '../../../../../components/layout/styled'
import { displayAsCurrency } from '../../../../../utils/displayAsCurrency'
import { displayDateAsText } from '../../../../../utils/displayDateAsText'
import { useAppTranslations } from '../../../../../utils/hooks/useAppTranslations'
import { useSnackbarManager } from '../../../../../utils/providers/snackbarContext'
import { ADGROUP_FLIGHT_DATE_FORMAT } from '../adgroupFlightDateFormat'
import { toUtcDateOnly } from './budgetPlanForm.utils'

interface BudgetPlansTableProps {
  adGroupId: string
  plans: AdGroupBudgetPlanPartsFragment[]
  onEdit?: (plan: AdGroupBudgetPlanPartsFragment) => void
  /** When true, hides the edit/delete actions column. */
  readOnly?: boolean
}

const formatPeriod = (plan: AdGroupBudgetPlanPartsFragment, unchangedLabel: string): string => {
  if (!plan.changeDates) return unchangedLabel
  const start = toUtcDateOnly(plan.startDate)
  const end = toUtcDateOnly(plan.endDate)
  const startLabel = start != null ? (displayDateAsText(start, ADGROUP_FLIGHT_DATE_FORMAT) ?? start) : '—'
  const endLabel = end != null ? (displayDateAsText(end, ADGROUP_FLIGHT_DATE_FORMAT) ?? end) : '—'
  return `${startLabel} → ${endLabel}`
}

const formatCost = (plan: AdGroupBudgetPlanPartsFragment, unchangedLabel: string): string => {
  if (!plan.changeCost) return unchangedLabel
  const type = plan.costType as CostType | null
  const value = plan.costValue
  const typeLabel = type ? type.toUpperCase() : '—'
  const valueLabel = value != null ? (displayAsCurrency(value) ?? String(value)) : '—'
  return `${typeLabel} ${valueLabel}`
}

const BudgetPlansTable: React.FC<BudgetPlansTableProps> = ({
  adGroupId,
  plans,
  onEdit,
  readOnly = false,
}): JSX.Element => {
  const t = useAppTranslations()
  const [, setSnackbar] = useSnackbarManager()
  const columnHelper = createColumnHelper<AdGroupBudgetPlanPartsFragment>()
  const [deletePlan, { loading: deleteLoading }] = useDeleteAdGroupBudgetPlanMutation({
    refetchQueries: [{ query: AdGroupBudgetPlansDocument, variables: { id: adGroupId } }],
    awaitRefetchQueries: true,
  })

  const unchangedLabel = t('adgroup.budgetAndDates.budgetPlans.unchanged')

  const handleDelete = async (plan: AdGroupBudgetPlanPartsFragment): Promise<void> => {
    try {
      await deletePlan({
        variables: {
          id: plan.id,
        },
      })
      setSnackbar({ severity: 'success', message: t('commons.saved') })
    } catch (error) {
      setSnackbar({
        severity: 'error',
        message: error instanceof Error ? error.message : t('commons.errorOccurred'),
      })
    }
  }

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('plannedDate', {
          header: t('adgroup.budgetAndDates.budgetPlans.plannedDate') as string,
          cell: ({ getValue }) => displayDateAsText(getValue() as string, ADGROUP_FLIGHT_DATE_FORMAT),
        }),
        columnHelper.display({
          id: 'cost',
          header: t('adgroup.budgetAndDates.budgetPlans.previewCost') as string,
          cell: ({ row }) => formatCost(row.original, unchangedLabel),
        }),
        columnHelper.accessor('dailyBudgetType', {
          header: t('adgroup.budgetAndDates.budgetTypeLabel') as string,
          cell: ({ getValue, row }) => {
            if (!row.original.changeDailyBudget) return unchangedLabel
            const value = getValue() as DailyBudgetType | null
            return value ? t(`adgroup.budgetAndDates.budgetType.${value}`) : unchangedLabel
          },
        }),
        columnHelper.accessor('dailyBudget', {
          header: t('adgroup.details.dailyBudgetWithCurrency') as string,
          cell: ({ getValue, row }) => {
            if (!row.original.changeDailyBudget) return unchangedLabel
            const value = getValue() as number | null
            return value != null ? displayAsCurrency(value) : unchangedLabel
          },
        }),
        columnHelper.accessor('budgetCappingType', {
          header: t('adgroup.budgetAndDates.budgetCappingType') as string,
          cell: ({ getValue, row }) => {
            if (!row.original.changeBudgetCapping) return unchangedLabel
            const value = getValue() as BudgetCapping | null
            return value ? t(`enum.BudgetCapping.${value}`) : unchangedLabel
          },
        }),
        columnHelper.accessor('budgetCapping', {
          header: t('adgroup.budgetAndDates.budgetCapping') as string,
          cell: ({ getValue, row }) => {
            if (!row.original.changeBudgetCapping) return unchangedLabel
            const value = getValue() as number | null
            return value != null ? displayAsCurrency(value) : unchangedLabel
          },
        }),
        columnHelper.display({
          id: 'period',
          header: t('adgroup.budgetAndDates.budgetPlans.previewPeriod') as string,
          cell: ({ row }) => formatPeriod(row.original, unchangedLabel),
        }),
        ...(!readOnly
          ? [
              columnHelper.display({
                id: 'actions',
                header: t('commons.actionsTitle') as string,
                cell: ({ row }) => {
                  const plan = row.original
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={t('adgroup.budgetAndDates.budgetPlans.edit')}>
                        <div>
                          <IconButton size="small" onClick={() => onEdit?.(plan)} data-testid="editBudgetPlanButton">
                            <Mode fontSize="small" sx={{ color: 'inherit !important' }} />
                          </IconButton>
                        </div>
                      </Tooltip>
                      <DeleteCellAction
                        onClick={() => {
                          void handleDelete(plan)
                        }}
                        tooltipLabel={t('adgroup.budgetAndDates.budgetPlans.delete')}
                        disabled={deleteLoading}
                      />
                    </Box>
                  )
                },
              }),
            ]
          : []),
      ] as Column[],
    [deleteLoading, onEdit, readOnly, t, unchangedLabel]
  )

  if (plans.length < 1) {
    return (
      <Grid container sx={{ mt: 1 }}>
        <StyledMessage variant="body2">{t('adgroup.budgetAndDates.budgetPlans.empty')}</StyledMessage>
      </Grid>
    )
  }

  return (
    <Box data-testid="budgetPlansTableScroll" sx={{ mt: 1, width: '100%', minWidth: 0 }}>
      <ReactTable columns={columns} data={plans} scrollX />
    </Box>
  )
}

export default BudgetPlansTable
